package diana.soleil.design.iraniancanadienbazzar.feature.home

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.BusinessRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.CategoryRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.CityController
import diana.soleil.design.iraniancanadienbazzar.core.data.FavoritesController
import diana.soleil.design.iraniancanadienbazzar.core.data.InterestsController
import diana.soleil.design.iraniancanadienbazzar.core.data.LocationController
import diana.soleil.design.iraniancanadienbazzar.core.data.NewsRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.NotificationRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.SponsorshipConfig
import diana.soleil.design.iraniancanadienbazzar.core.model.Business
import diana.soleil.design.iraniancanadienbazzar.core.model.Category
import diana.soleil.design.iraniancanadienbazzar.core.model.Country
import diana.soleil.design.iraniancanadienbazzar.core.model.DiscoveryRanking
import diana.soleil.design.iraniancanadienbazzar.core.model.LocalizedText
import diana.soleil.design.iraniancanadienbazzar.core.model.NewsArticle
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.catch
import diana.soleil.design.iraniancanadienbazzar.core.data.ChatRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.DealsRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.EventRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.FeatureFlags
import diana.soleil.design.iraniancanadienbazzar.core.model.Deal
import diana.soleil.design.iraniancanadienbazzar.core.model.Event
import diana.soleil.design.iraniancanadienbazzar.core.model.PublicProfile
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.stateIn

/**
 * One line of the Home activity ticker. Typed (not pre-formatted text) so the SCREEN localizes via
 * string resources; every variant is derived from already-public data (privacy-safe by construction).
 */
sealed interface TickerItem {
    /** A live promotion: "{businessName} — {title}". */
    data class DealPosted(val businessName: String, val title: LocalizedText) : TickerItem

    /** Someone opted into Find your people: "{firstName} joined…". Public-by-choice display name only. */
    data class PersonJoined(val firstName: String) : TickerItem

    /** A community news headline. */
    data class NewsPublished(val title: LocalizedText) : TickerItem
}

/**
 * The four ADDITIVE Home streams, bundled so the main `combine` stays within arity and reads clearly.
 * Every one of these degrades to empty on failure or flag-off — only categories/featured/businesses
 * are fatal to the screen.
 */
private data class HomeExtras(
    val news: DataResult<List<NewsArticle>>,
    val deals: List<Deal>,
    val people: List<PublicProfile>,
    val events: List<Event>,
)

/** UI state for Home/Discover. */
data class HomeUiState(
    val loading: Boolean = true,
    val categoryShortcuts: List<Category> = emptyList(),
    val featured: List<Business> = emptyList(),
    /** Businesses matching the user's onboarding/profile interests; empty when none picked / no match. */
    val forYou: List<Business> = emptyList(),
    val popular: List<Business> = emptyList(),
    /** Community news for the carousel; the section is hidden when empty. */
    val news: List<NewsArticle> = emptyList(),
    /** Upcoming events for the Home rail (soonest first); the section hides when empty / flag off. */
    val events: List<Event> = emptyList(),
    /** Live merchant promotions (soonest-ending first); the rail hides when empty / flag off. */
    val deals: List<Deal> = emptyList(),
    /** Discoverable members for the "Meet people near you" rail; hides when empty / flag off. */
    val people: List<PublicProfile> = emptyList(),
    /** Rotating "what's happening" strip, derived from the public streams above; hides when empty. */
    val ticker: List<TickerItem> = emptyList(),
    /** Metro ids that are live for rollout (Remote Config `active_launch_cities`); others = "coming soon". */
    val launchedMetros: List<String> = listOf("gta", "mtl", "yvr"),
    /** Active country — scopes the city switcher's metro list + the "All \<country\>" label. */
    val activeCountry: Country = Country.CA,
    val error: Boolean = false,
)

/**
 * Home/Discover state holder: a few category shortcuts, sponsored "featured" carousel, and "popular"
 * list — all from the repositories, scoped to the active metro (null = All Canada until the city
 * controller lands). See `docs/LOCALIZATION.md`.
 */
@OptIn(ExperimentalCoroutinesApi::class)
class HomeViewModel(
    private val businessRepository: BusinessRepository,
    private val categoryRepository: CategoryRepository,
    private val cityController: CityController,
    private val sponsorshipConfig: SponsorshipConfig,
    notificationRepository: NotificationRepository,
    private val newsRepository: NewsRepository,
    private val dealsRepository: DealsRepository,
    private val chatRepository: ChatRepository,
    private val eventRepository: EventRepository,
    private val featureFlags: FeatureFlags,
    private val favoritesController: FavoritesController,
    private val locationController: LocationController,
    private val interestsController: InterestsController,
) : ViewModel() {

    private val retryTrigger = MutableStateFlow(0)

    /** Saved (favorited) business ids; "Popular near you" hearts render and toggle off this. */
    val favorites: StateFlow<Set<String>> = favoritesController.favorites

    /** Save/unsave a business (persisted; guests stay device-local). Reflected via [favorites]. */
    fun toggleFavorite(businessId: String) = favoritesController.toggle(businessId)

    /** Unread notification count for the bell badge; best-effort (0 for guests / on error). */
    val unreadCount: StateFlow<Int> =
        notificationRepository.observeNotifications()
            .map { result -> (result as? DataResult.Success)?.data?.count { !it.read } ?: 0 }
            .catch { emit(0) }
            .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), 0)

    // Re-queries whenever the active metro OR country changes (or on retry); null metro = "All
    // <country>", scoped by the active country. The launched-cities gate is folded in separately so an
    // RC update doesn't trigger a re-query.
    val state: StateFlow<HomeUiState> =
        combine(
            combine(
                cityController.activeMetroId,
                cityController.activeCountry,
                retryTrigger,
            ) { metroId, country, _ -> metroId to country }
                .flatMapLatest { (metroId, country) -> homeFlow(metroId, country.code) },
            cityController.launchedMetros,
            cityController.activeCountry,
        ) { ui, launched, country -> ui.copy(launchedMetros = launched, activeCountry = country) }
            .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), HomeUiState())

    /** Active metro (null = "All [country]") for the city selector. */
    val activeMetroId: StateFlow<String?> = cityController.activeMetroId

    fun selectMetro(metroId: String?) = cityController.setMetro(metroId)

    /** Switch the active country from the city sheet's toggle (re-scopes the metro list + directory). */
    fun selectCountry(country: Country) = cityController.setCountry(country)

    fun retry() {
        retryTrigger.value += 1
    }

    private fun homeFlow(metroId: String?, countryCode: String): Flow<HomeUiState> = combine(
        combine(
            categoryRepository.observeCategories(),
            businessRepository.observeFeatured(metroId, countryCode),
            businessRepository.observeBusinesses(metroId, country = countryCode),
        ) { categories, featured, all -> Triple(categories, featured, all) },
        sponsorshipConfig.slotsMax,
        // News is additive: a failure just hides the section, never errors the whole Home.
        combine(
            newsRepository.observeNews(metroId).catch { emit(DataResult.Success(emptyList())) },
            // Deals are additive like news: flag-off or failure just hides the rail.
            combine(
                featureFlags.available.flatMapLatest { flags ->
                    if (!flags.deals) flowOf(emptyList())
                    else dealsRepository.observeActiveDeals(DEALS_RAIL_COUNT)
                        .map { (it as? DataResult.Success)?.data ?: emptyList() }
                        .catch { emit(emptyList()) }
                },
                featureFlags.available.flatMapLatest { flags ->
                    if (!flags.people) flowOf(emptyList())
                    else chatRepository.observeDiscoverablePeople(limit = PEOPLE_RAIL_COUNT)
                        .map { (it as? DataResult.Success)?.data ?: emptyList() }
                        .catch { emit(emptyList()) }
                },
            ) { deals, people -> deals to people },
            // Events are additive too — a failure or flag-off hides the rail, never errors Home.
            featureFlags.available.flatMapLatest { flags ->
                if (!flags.events) flowOf(emptyList())
                else eventRepository.observeUpcomingEvents(metroId)
                    .map { (it as? DataResult.Success)?.data.orEmpty().take(EVENTS_RAIL_COUNT) }
                    .catch { emit(emptyList()) }
            },
        ) { news, extras, events -> HomeExtras(news, extras.first, extras.second, events) },
        // Re-ranks in place once the GPS fix lands (metro centre until then / when denied).
        locationController.location,
        // Onboarding/Profile interest picks gently bias ordering (never filter — discovery stays broad).
        interestsController.interests,
    ) { (categories, featured, all), slotsMax, extras, userLocation, interests ->
        val (news, deals, people, events) = extras
        if (categories is DataResult.Failure || featured is DataResult.Failure || all is DataResult.Failure) {
            HomeUiState(loading = false, error = true)
        } else {
            // Appeal-first ranking (photo → nearby → rating) for both the sponsored carousel and the
            // "popular" teaser; the carousel is capped to the Remote Config slot budget and overflow
            // sponsored simply fall back into the normal "popular" list (still labeled where shown).
            val order = DiscoveryRanking.businessComparator(DiscoveryRanking.origin(userLocation, metroId))
            val featuredList = (featured as DataResult.Success).data
                .sortedWith(order)
                .take(slotsMax.coerceAtLeast(0))
            val featuredIds = featuredList.map { it.id }.toSet()
            val allBusinesses = (all as DataResult.Success).data
            // "For you": a dedicated personalized rail of interest-matching businesses (ranked, capped).
            // Empty when the user picked no interests or nothing matches — the section then hides.
            val forYouList = if (interests.isEmpty()) {
                emptyList()
            } else {
                allBusinesses
                    .filter { it.categoryId in interests && it.id !in featuredIds }
                    .sortedWith(order)
                    .take(FOR_YOU_COUNT)
            }
            val forYouIds = forYouList.map { it.id }.toSet()
            HomeUiState(
                loading = false,
                // Interest categories float to the front of the grid (stable — seeded order kept
                // within each group) so a personalized pick is always visible in the 12-tile cut.
                categoryShortcuts = (categories as DataResult.Success).data
                    .sortedBy { if (it.id in interests) 0 else 1 }
                    .take(SHORTCUT_COUNT),
                featured = featuredList,
                forYou = forYouList,
                // Home is a discovery teaser, not the directory: rank and cap the section.
                // Composing the whole metro (100+ cards) in Home's non-lazy scroll is a jank bomb;
                // the full list lives behind Categories / Search (lazy, paged screens).
                // Interest matches now have their own "For you" rail, so Popular is broad discovery
                // beyond what the user already told us they like (featured + for-you excluded).
                popular = allBusinesses
                    .filter { it.id !in featuredIds && it.id !in forYouIds }
                    .sortedWith(order)
                    .take(POPULAR_COUNT),
                news = (news as? DataResult.Success)?.data.orEmpty()
                    .sortedWith(DiscoveryRanking.newsComparator()),
                deals = deals,
                people = people,
                events = events,
                ticker = buildTicker(deals, people, (news as? DataResult.Success)?.data.orEmpty()),
            )
        }
    }

    /**
     * Interleaves the public streams into a varied rotation (deal → person → headline → …). Pure
     * derivation — the ticker never queries anything the Home sections don't already show.
     */
    private fun buildTicker(
        deals: List<Deal>,
        people: List<PublicProfile>,
        news: List<NewsArticle>,
    ): List<TickerItem> {
        // Drop anything that would render as a line with a hole in it. dev has a discoverable
        // publicProfile with a null displayName, which reaches "%1$s joined Find your people" as an
        // empty first name and renders " joined Find your people". Deals and news get the same guard
        // for the same reason — the formats interpolate a name/title and read as broken without one.
        // Filter BEFORE take(), otherwise one junk row silently costs a slot and the ticker gets
        // shorter instead of skipping it. LocalizedText.isEmpty means "no content in ANY language",
        // which is the right test because resolve() falls back across all three.
        val dealItems = deals.asSequence()
            .filter { it.businessName.isNotBlank() && !it.title.isEmpty }
            .take(TICKER_PER_SOURCE)
            .map { TickerItem.DealPosted(it.businessName, it.title) }
            .toList()
        val peopleItems = people.asSequence()
            .map { it.displayName.substringBefore(' ').trim() }
            .filter { it.isNotBlank() }
            .take(TICKER_PER_SOURCE)
            .map { TickerItem.PersonJoined(it) }
            .toList()
        val newsItems = news.asSequence()
            .filter { !it.title.isEmpty }
            .take(TICKER_PER_SOURCE)
            .map { TickerItem.NewsPublished(it.title) }
            .toList()
        val sources = listOf(dealItems, peopleItems, newsItems)
        return (0 until TICKER_PER_SOURCE).flatMap { i -> sources.mapNotNull { it.getOrNull(i) } }
    }

    private companion object {
        // Home shows a category GRID (3 rows × 4 on phones), not a 4-item scroll row — the grid is
        // the home centerpiece, so nearly the full taxonomy (15) is one tap away.
        const val SHORTCUT_COUNT = 12
        // "Popular near you" teaser size. Also keeps Home's scroll content well under the ~4000dp
        // Android clipped-layer hit-test limit documented in HomeScreen's sheet comment.
        const val POPULAR_COUNT = 20
        // "For you" personalized rail size (interest-matching businesses).
        const val FOR_YOU_COUNT = 10
        const val EVENTS_RAIL_COUNT = 10
        const val DEALS_RAIL_COUNT = 10
        const val PEOPLE_RAIL_COUNT = 12
        // Per-source cap keeps the rotation short enough that a user actually sees it cycle.
        const val TICKER_PER_SOURCE = 3
    }
}
